import type { DocumentSession } from '../core/session';
import { saveRecovery } from './recovery';

/** Observe one document independently of which workspace tab is active. */
export function watchRecoveryAutosave(session: DocumentSession, callbacks: {
  onSaved(): void;
  onFailure(): void;
}, delayMs = 1_800): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let pending: AbortController | undefined;
  const cancel = () => {
    clearTimeout(timer); timer = undefined;
    pending?.abort(); pending = undefined;
  };
  const schedule = () => {
    cancel();
    if (stopped || !session.dirty || session.history.hasLiveCommand
      || (session.recovery.status === 'saved' && session.recovery.revision === session.currentRevision)) return;
    timer = setTimeout(() => {
      timer = undefined;
      const controller = new AbortController(); pending = controller;
      void saveRecovery(session, controller.signal).then(() => {
        if (!stopped && !controller.signal.aborted) callbacks.onSaved();
      }).catch(() => {
        if (!stopped && !controller.signal.aborted) callbacks.onFailure();
      }).finally(() => { if (pending === controller) pending = undefined; });
    }, delayMs);
  };
  const unsubscribe = session.subscribe(schedule);
  const stop = () => { stopped = true; unsubscribe(); cancel(); session.closedSignal.removeEventListener('abort', stop); };
  session.closedSignal.addEventListener('abort', stop, { once: true });
  if (session.closedSignal.aborted) stop(); else schedule();
  return stop;
}
